/** One object the Lab detected in an asset, in the segments' work coordinates */
export interface LabSegmentInstance {
  id: number
  concept: string
  score: number
  /** [x0, y0, x1, y1], inclusive */
  bbox: [number, number, number, number]
  /** base64 grey PNG of the bbox crop: white = the object, black = not */
  png: string
}

export interface LabSegments {
  /** Work size of the label map and boxes (long edge ≤ 1024, same aspect ratio as the asset) */
  w: number
  h: number
  /** base64 8-bit grey PNG, w × h: each pixel holds the id of the object under it (0 = none) */
  labelsPng: string
  instances: LabSegmentInstance[]
}

export type LabSegmentsResult = { status: 'ready', segments: LabSegments } | { status: 'pending' | 'failed' | 'none' }

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

const parseInstance = (value: unknown): LabSegmentInstance[] => {
  const { id, concept, score, bbox, png } = (value ?? {}) as Record<string, unknown>
  const isValid = isNumber(id) && id > 0 && typeof png === 'string' && png.length > 0
    && Array.isArray(bbox) && bbox.length === 4 && bbox.every(isNumber)
  return isValid
    ? [{ id, concept: typeof concept === 'string' ? concept : '', score: isNumber(score) ? score : 0, bbox: bbox as LabSegmentInstance['bbox'], png }]
    : []
}

/**
 * The result of an `objects` job — the Lab's segments JSON, which the platform stores as the job's result. The Lab wraps
 * it in `{ data }` (both shapes are read); anything malformed reads as failed, so the eraser falls back to the brush.
 */
export const parseLabSegments = (body: unknown): LabSegmentsResult => {
  const data = ((body as { data?: unknown } | undefined)?.data ?? body ?? {}) as Record<string, unknown>
  const { status } = data
  if (status === 'pending' || status === 'none' || status === 'failed') return { status }
  if (status !== 'ready') return { status: 'failed' }
  const { w, h, labelsPng, instances } = data
  if (!isNumber(w) || !isNumber(h) || w <= 0 || h <= 0 || typeof labelsPng !== 'string' || !Array.isArray(instances)) return { status: 'failed' }
  return { status: 'ready', segments: { w, h, labelsPng, instances: instances.flatMap(parseInstance) } }
}
